import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import mermaid from "mermaid";
import { cn } from "~/lib/utils";
import { useConversationId } from "~/pages/manifold-nextgen/components/conversations/ConversationContext";
import { useArtifactLineage } from "./useArtifactLineage";

interface ArtifactLineageProps {
  artifactId: string;
}

// Lazy-init mermaid once per module load. The legacy LineageTab does this
// inside an effect; doing it at module scope is cheaper and equivalent
// because mermaid.initialize is idempotent.
let mermaidReady = false;
function ensureMermaid() {
  if (mermaidReady) return;
  mermaid.initialize({ startOnLoad: false, theme: "default" });
  mermaidReady = true;
}

// Renders the mermaid diagram returned by the lineage endpoint as inline
// SVG. Mounts as the body of an ArtifactShell when the toolbar's Lineage
// toggle is on — exact parallel to ArtifactCode.
export const ArtifactLineage = ({ artifactId }: ArtifactLineageProps) => {
  const conversationId = useConversationId();
  const { lineage, error, isLoading } = useArtifactLineage(conversationId, artifactId);
  const containerRef = useRef<HTMLDivElement>(null);
  const [renderError, setRenderError] = useState<string | null>(null);

  useEffect(() => {
    if (!lineage?.mermaid || !containerRef.current) return;
    ensureMermaid();
    const el = containerRef.current;
    const diagramId = `lineage-${artifactId}-${Date.now()}`;
    let cancelled = false;
    mermaid
      .render(diagramId, lineage.mermaid)
      .then(({ svg }) => {
        if (cancelled) return;
        el.innerHTML = svg;
        setRenderError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setRenderError(err instanceof Error ? err.message : "Failed to render lineage diagram");
      });
    return () => {
      cancelled = true;
    };
  }, [lineage, artifactId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2
          className="size-5 animate-spin text-muted-foreground"
          aria-label="Loading lineage"
        />
      </div>
    );
  }
  if (error) {
    return <p className="py-3 text-xs text-destructive">Failed to load lineage: {String(error)}</p>;
  }
  if (!lineage || !lineage.nodes?.length) {
    return (
      <p className="py-3 text-xs text-muted-foreground">No lineage found for this artifact.</p>
    );
  }
  if (renderError) {
    return <p className="py-3 text-xs text-destructive">{renderError}</p>;
  }
  return (
    <div
      ref={containerRef}
      className={cn(
        "flex max-h-72 items-center justify-center overflow-auto",
        "bg-background/80 px-3 py-2"
      )}
    />
  );
};
